import { useEffect, useRef, useState, type FormEvent } from "react";
import { calculateResultingExpiry } from "@cheemspay/domain";
import { ApiRequestError, apiRequest } from "../api";
import { Button, Field, StatusPill } from "../components";
import { useAdminUrlState } from "./admin-navigation";
import { clearStoredManualGrant, completeStoredManualGrant, prepareStoredManualGrant, readStoredManualGrant,
  type StoredGrantReceipt, type StoredGrantRequest } from "./manual-grant-operation";

interface GrantProduct {
  productId: string;
  appId: string;
  code: string;
  name: string;
  active: boolean;
  features: string[];
  maxDevices: number;
}

export interface AdminEntitlementGrantResult {
  grantId: string;
  entitlementId: string;
  username: string;
  appId: string;
  productCode: string;
  productName: string;
  durationDays: number;
  resultingExpiry: string;
  idempotent: boolean;
}

interface EntitlementLookup {
  user: { id: string; username: string; role: string | null; createdAt: string };
  entitlements: Array<{
    id: string; appId: string; productCode: string; productName: string;
    status: string; active: boolean; expiresAt: string | null; version: number; features: string[];
  }>;
  grants: Array<{
    id: string; appId: string; productCode: string; productName: string; grantType: string;
    durationSeconds: number; manualRef: string | null; previousExpiry: string | null;
    resultingExpiry: string | null; status: string; createdAt: string;
  }>;
}

const newManualRef = () => `admin-${crypto.randomUUID()}`;
const displayExpiry = (expiry: string | null) => expiry ? new Date(expiry).toLocaleString() : "长期有效";

export function AdminEntitlementGrantForm({ products, onGranted }: {
  products: GrantProduct[];
  onGranted: (result: AdminEntitlementGrantResult) => void;
}) {
  // This workspace has always been Bomana-only. Do not accidentally offer a different app's rights.
  const bomanaProducts = products.filter((product) => product.appId === "bomana");
  const defaultProduct = bomanaProducts.find((product) => product.active)?.code ?? bomanaProducts[0]?.code ?? "";
  const [username, setUsername] = useAdminUrlState("grants.username", "");
  const [productCode, setProductCode] = useState(defaultProduct);
  const [durationDays, setDurationDays] = useState("");
  const [manualRef, setManualRef] = useState(newManualRef);
  const [reason, setReason] = useState("管理员人工增加 Bomana 订阅");
  const [busy, setBusy] = useState(false);
  const mounted = useRef(false);
  const submitting = useRef(false);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookup, setLookup] = useState<EntitlementLookup | null>(null);
  const lookupVersion = useRef(0);
  const lookupController = useRef<AbortController | null>(null);
  const [grantConfirmed, setGrantConfirmed] = useState(false);
  const [recovery, setRecovery] = useState(() => {
    try { return { operation: readStoredManualGrant(), error: "" }; }
    catch { return { operation: null, error: "无法读取本页保存的授权凭据。请先在发放记录核对上次操作，再恢复会话存储；暂不发起新授权。" }; }
  });
  const [pendingOperation, setPendingOperation] = useState<StoredGrantRequest | null>(null);
  const [completed, setCompleted] = useState<StoredGrantReceipt | null>(null);
  const [error, setError] = useState("");
  const normalizedUsername = (pendingOperation?.username ?? username).trim().toLowerCase();
  const product = bomanaProducts.find((candidate) => candidate.code === productCode);
  const currentLookup = lookup?.user.username === normalizedUsername ? lookup : null;
  const selectedRecord = currentLookup?.entitlements.find((item) => item.productCode === productCode);
  const otherActiveRecords = currentLookup?.entitlements.filter((item) => item.productCode !== productCode && item.active) ?? [];
  const hasLifetimeRecord = selectedRecord?.expiresAt === null;
  const days = Number(durationDays);
  const validDays = Number.isSafeInteger(days) && days >= 1 && days <= 20 * 365;
  const needsRecovery = recovery.operation !== null || Boolean(recovery.error);
  const formLocked = busy || completed !== null || needsRecovery;
  const payloadLocked = formLocked || pendingOperation !== null;

  const invalidateLookup = () => {
    lookupVersion.current += 1;
    lookupController.current?.abort();
    setLookup(null);
    setLookupBusy(false);
    setGrantConfirmed(false);
  };

  // URL Back/Forward can change the username without an input change event.
  useEffect(() => { invalidateLookup(); }, [username]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      lookupVersion.current += 1;
      lookupController.current?.abort();
    };
  }, []);

  const loadLookup = async (requestedUsername: string) => {
    if (!mounted.current) return;
    const requested = requestedUsername.trim().toLowerCase();
    invalidateLookup();
    if (!/^[a-zA-Z0-9._]{3,30}$/.test(requested)) {
      setError("请输入 3-30 位用户名后再查询");
      return;
    }
    const version = lookupVersion.current;
    const controller = new AbortController();
    lookupController.current = controller;
    setLookupBusy(true);
    setError("");
    try {
      const result = await apiRequest<EntitlementLookup>(
        `/api/admin/users/${encodeURIComponent(requested)}/entitlements?appId=bomana`,
        { signal: controller.signal },
      );
      if (mounted.current && version === lookupVersion.current) setLookup(result);
    } catch (caught) {
      if (!mounted.current || version !== lookupVersion.current) return;
      setError(caught instanceof ApiRequestError && caught.code === "NOT_FOUND"
        ? `未找到用户 ${requested}`
        : caught instanceof Error ? caught.message : "用户权益查询失败");
    } finally {
      if (mounted.current && version === lookupVersion.current) setLookupBusy(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting.current || completed || needsRecovery) return;
    if (!/^[a-zA-Z0-9._]{3,30}$/.test(normalizedUsername) || !validDays || (!product && !pendingOperation)
      || !/^[a-zA-Z0-9._:-]{1,120}$/.test(manualRef.trim())
      || reason.trim().length < 3 || reason.trim().length > 500) {
      setError("请检查用户名、授权记录、实际增加天数、人工凭据和原因（至少 3 个字）");
      return;
    }
    if (!currentLookup || lookupBusy) {
      setError("请先查询该用户名的当前权益，再确认增加订阅");
      return;
    }
    if (hasLifetimeRecord && !pendingOperation) {
      setError("所选记录长期有效，无需增加天数；请勿将长期授权改为有限时长");
      return;
    }
    if (!grantConfirmed) {
      setError("请先复核目标账号、授权记录和实际增加天数，并勾选确认");
      return;
    }
    submitting.current = true;
    setBusy(true);
    setError("");
    const firstAttempt = pendingOperation === null;
    const request: StoredGrantRequest = pendingOperation ?? { username: normalizedUsername, appId: "bomana", productCode,
      durationDays: days, manualRef: manualRef.trim() };
    try {
      const priorReceipt = prepareStoredManualGrant(request, pendingOperation !== null);
      setPendingOperation(request);
      if (priorReceipt) {
        setCompleted(priorReceipt);
        setGrantConfirmed(false);
        submitting.current = false;
        setBusy(false);
        return;
      }
    } catch {
      submitting.current = false;
      setBusy(false);
      setError("无法保存或恢复原操作凭据，尚未发送授权请求。请刷新并核对已保存操作，确认本页会话存储可用后重试。");
      return;
    }
    try {
      const result = await apiRequest<AdminEntitlementGrantResult>("/api/admin/entitlements/grant", {
        method: "POST",
        body: JSON.stringify({ ...request, reason: reason.trim() }),
      });
      // If saving the receipt fails, the already-saved request still provides a safe same-key retry.
      try { completeStoredManualGrant(request, result); } catch { /* Keep the unresolved request for recovery. */ }
      if (!mounted.current) return;
      setCompleted(result);
      setGrantConfirmed(false);
      onGranted(result);
      // A refresh failure cannot turn a successful write into an invitation to submit a new grant.
      await loadLookup(normalizedUsername);
    } catch (caught) {
      let rejected = false;
      if (mounted.current && firstAttempt && caught instanceof ApiRequestError && caught.status >= 400 && caught.status < 500 && [
        "INVALID_REQUEST", "NOT_FOUND", "MANUAL_GRANT_DURATION_INVALID", "MANUAL_GRANT_LIFETIME_ENTITLEMENT",
        "MANUAL_GRANT_REF_CONFLICT", "ADMIN_2FA_REQUIRED", "AUTHENTICATION_REQUIRED",
      ].includes(caught.code)) {
        // Only this still-mounted first attempt can settle its own rejection.
        // A retry's rejection cannot settle an older ambiguous/in-flight attempt.
        try { rejected = clearStoredManualGrant(request); } catch { /* Keep the safe retry key. */ }
      }
      if (!mounted.current) return;
      if (rejected) setPendingOperation(null);
      setGrantConfirmed(false);
      setError(`${caught instanceof Error ? caught.message : "人工订阅增加失败"}。如需重试，请保留同一人工凭据，以免重复增加时长。`);
    } finally {
      submitting.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  return <article className="dashboard-card admin-grant-form">
    <div className="card-title-row"><div>
      <span className="card-kicker">人工授权</span><h2>给账号增加订阅</h2>
      <p className="admin-card-note">授权对象：Bomana 订阅。实际增加多久，只取决于下方填写的天数；商品名称中的“一年 / 三天”是销售套餐名称。</p>
      <p className="admin-card-note">所有 Bomana 订阅统一使用全局功能和设备规则。不同商品的授权记录仍分别累计到期日，请先查询账号再选择要延长的记录。</p>
    </div></div>
    <form className="product-editor" onSubmit={submit}>
      {recovery.error ? <p className="form-error" role="alert">{recovery.error}</p> : null}
      {recovery.operation ? <div className="risk-box" role="status">
        <p>已保存上次授权操作：{recovery.operation.request.username} · {recovery.operation.request.productCode} · {recovery.operation.request.durationDays} 天 · 凭据 {recovery.operation.request.manualRef}。
          {recovery.operation.receipt ? "已有成功结果，请恢复查看后再开始下一次授权。" : "结果尚未确认，请恢复原凭据后核对；不要新建凭据重复发放。"}</p>
        <Button type="button" onClick={() => {
          const saved = recovery.operation!;
          invalidateLookup(); setUsername(saved.request.username); setProductCode(saved.request.productCode);
          setDurationDays(String(saved.request.durationDays)); setManualRef(saved.request.manualRef); setReason("");
          setPendingOperation(saved.request); setCompleted(saved.receipt ?? null);
          setRecovery({ operation: null, error: "" }); setError("");
        }}>恢复上次授权核对</Button>
      </div> : null}
      {pendingOperation && !completed ? <p className="risk-box">当前沿用已保存的授权凭据。目标账号、记录和天数已锁定；请查询当前权益并复核后重试。
        刷新或离开工作区后可恢复该操作，后台原因不会保存在浏览器中，恢复时需重新填写。</p> : null}
      <div className="product-editor-grid">
        <Field label="用户名"><input autoComplete="off" disabled={payloadLocked} required
          value={pendingOperation?.username ?? username} placeholder="例如 qwq" onChange={(event) => {
            invalidateLookup(); setUsername(event.target.value); setError("");
          }} /></Field>
        <div className="admin-lookup-action"><Button className="secondary-button" disabled={lookupBusy || busy}
          onClick={() => void loadLookup(normalizedUsername)} type="button">{lookupBusy ? "查询中…" : "查询当前权益"}</Button></div>
        <Field label="实际增加天数" hint="填写 1–7300 的整数；没有默认值，不使用销售套餐时长。1 天按 24 小时计算。">
          <input inputMode="numeric" min="1" max={20 * 365} step="1" type="number" required disabled={payloadLocked}
            value={durationDays} onChange={(event) => { setDurationDays(event.target.value); setGrantConfirmed(false); }} />
        </Field>
        <Field label="延长哪条授权记录" hint="关联商品用于识别原授权记录，不决定本次增加天数；选择另一条记录不会延长原记录。">
          <select required disabled={payloadLocked} value={productCode}
            onChange={(event) => { setProductCode(event.target.value); setGrantConfirmed(false); }}>
            {bomanaProducts.map((item) => <option key={item.productId} value={item.code}>
              {item.name} · {item.code}{item.active ? "" : "（已下架）"}
            </option>)}
            {pendingOperation && !product ? <option value={pendingOperation.productCode}>{pendingOperation.productCode}（恢复原记录）</option> : null}
          </select>
        </Field>
        <Field label="人工凭据" hint="同一次操作及失败重试保持此凭据；成功后用“开始下一次授权”生成新凭据。">
          <input autoComplete="off" required maxLength={120} pattern="[a-zA-Z0-9._:\-]+" disabled={payloadLocked}
            value={manualRef} onChange={(event) => { setManualRef(event.target.value); setGrantConfirmed(false); }} />
        </Field>
        <Field label="后台原因" hint="至少 3 个字，将写入审计日志。">
          <textarea required minLength={3} maxLength={500} rows={3} disabled={formLocked}
            value={reason} onChange={(event) => { setReason(event.target.value); setGrantConfirmed(false); }} />
        </Field>
      </div>
      {product ? <p className="risk-box">本次授权：Bomana 订阅 · 实际增加 {validDays ? days : "待填写"} 天
        <br />适用范围：功能和设备上限统一采用 Bomana 当前全局权益规则，销售套餐不区分功能等级
        <br />关联销售套餐：{product.name}（{product.code}），套餐标称时长不参与本次计算</p> :
        <p role="alert" className="form-error">{pendingOperation ? "当前商品目录未显示原授权记录，仅可沿用原凭据核对上次请求。" : "暂无可用的 Bomana 授权记录，请刷新商品数据。"}</p>}
      {currentLookup ? <div className="admin-lookup-result">
        <div className="admin-lookup-heading"><strong>{currentLookup.user.username}</strong>
          <small>注册于 {new Date(currentLookup.user.createdAt).toLocaleString()}</small></div>
        <div className="admin-lookup-entitlements">
          {currentLookup.entitlements.length ? currentLookup.entitlements.map((entitlement) =>
            <div className="admin-lookup-row" key={entitlement.id}>
              <span><strong>Bomana 订阅记录</strong><small>关联商品：{entitlement.productName} · {entitlement.productCode}</small></span>
              <span><StatusPill tone={entitlement.active ? "good" : "warn"}>{entitlement.active ? "生效中" : "已失效"}</StatusPill>
                <small>{displayExpiry(entitlement.expiresAt)}</small></span>
            </div>) : <small>当前没有记录的 Bomana 权益</small>}
        </div>
        {currentLookup.grants.length ? <details className="admin-lookup-history">
          <summary>最近 {Math.min(currentLookup.grants.length, 5)} 条发放记录</summary>
          {currentLookup.grants.slice(0, 5).map((grant) => <div className="admin-lookup-row" key={grant.id}>
            <span><strong>{grant.grantType === "MANUAL_ADMIN" ? "人工授权" : "授权发放"} · 实际增加 {grant.durationSeconds / 86_400} 天</strong>
              <small>关联商品：{grant.productName} · {grant.productCode}</small><small>{grant.manualRef ?? grant.grantType}</small></span>
            <small>{grant.status === "APPLIED" ? "已生效" : grant.status} · {new Date(grant.createdAt).toLocaleString()}</small>
          </div>)}
        </details> : null}
      </div> : null}
      {currentLookup && !completed ? <>
        {pendingOperation ? <p className="risk-box">正在核对原授权请求，原请求为 {pendingOperation.durationDays} 天。
          当前记录到期日：{selectedRecord ? displayExpiry(selectedRecord.expiresAt) : "无该商品的授权记录"}。
          若原请求已生效，重试只返回原结果，不会再次增加天数；若尚未生效，服务器才会按原请求处理。</p> :
        <p className="risk-box">所选记录到期日预览：{selectedRecord ? displayExpiry(selectedRecord.expiresAt) : "无该商品的授权记录"} → {hasLifetimeRecord
          ? "长期有效，无需增加"
          : validDays ? calculateResultingExpiry(new Date(), selectedRecord?.expiresAt ? new Date(selectedRecord.expiresAt) : null, days * 86_400).toLocaleString()
            : "请填写有效天数"}。未到期时从所选记录的到期日续加，否则从提交时起算。最终时间以服务器计算为准。</p>}
        {hasLifetimeRecord ? <p role="alert" className="form-error">{pendingOperation
          ? "当前记录长期有效，仅允许沿用原凭据核对上次请求；服务器不会将长期授权改为有限时长。"
          : "所选记录长期有效，无需增加天数；为避免缩短授权，本表单不会提交。"}</p> : null}
        {otherActiveRecords.length ? <p className="risk-box">该账号还有 {otherActiveRecords.length} 条其他有效授权记录，本次不会延长它们。各记录的有效期可能重叠，请核对后再提交。</p> : null}
        {selectedRecord && ["SUSPENDED", "REVOKED"].includes(selectedRecord.status) ?
          <p className="risk-box">所选记录已暂停或撤销；{pendingOperation ? "若原请求尚未生效，重试" : "本次人工授权"}会将该记录恢复为生效状态，请确认处理原因。</p> : null}
        <label className="admin-grant-confirmation risk-box"><input type="checkbox" checked={grantConfirmed}
          disabled={formLocked || !validDays || (!product && !pendingOperation) || (hasLifetimeRecord && !pendingOperation) || lookupBusy}
          onChange={(event) => { setGrantConfirmed(event.target.checked); setError(""); }} />
          <span><strong>{pendingOperation ? "复核并重试原授权请求" : "复核后立即增加权益"}</strong>
            {pendingOperation ? <>我确认核对账号 {currentLookup.user.username} 的原请求：Bomana 订阅 {days} 天，关联记录 {productCode}，凭据 {pendingOperation.manualRef}。
              已生效时仅返回原结果，不重复增加时长；未生效时按原请求发放。</> :
              <>我确认账号为 {currentLookup.user.username}，为 Bomana 订阅实际增加 {validDays ? days : "待填写"} 天。
                仅延长关联商品 {product?.name ?? "待选择"}（{productCode}）的授权记录；提交会改变该记录到期日并写入审计日志。</>}
          </span>
        </label>
      </> : null}
      {completed ? <p role="status" className="risk-box">本次授权已{completed.idempotent ? "幂等确认，未重复增加" : "完成"}：{completed.username} · 实际增加 {completed.durationDays} 天。
        已锁定本次凭据 {manualRef}，再次发放请明确开始下一次授权。</p> : null}
      {error ? <div className="form-error" role="alert">{error}</div> : null}
      <div className="product-editor-actions">{completed ?
        <Button type="button" disabled={busy} onClick={() => {
          try {
            if (!pendingOperation || !clearStoredManualGrant(pendingOperation, true)) {
              setError("已保存的授权操作发生变化，请刷新后先恢复并核对当前操作。"); return;
            }
          }
          catch { setError("无法清除已完成操作的会话凭据，暂不开始新授权，请恢复本页会话存储后重试。"); return; }
          setPendingOperation(null); setCompleted(null); setManualRef(newManualRef()); setDurationDays(""); invalidateLookup(); setError("");
        }}>开始下一次授权</Button> :
        <Button type="submit" disabled={needsRecovery || busy || lookupBusy || (!product && !pendingOperation) || !currentLookup || !validDays || (hasLifetimeRecord && !pendingOperation) || !grantConfirmed}>
          {busy ? "提交中…" : pendingOperation ? "核对并重试原授权" : "确认增加订阅"}
        </Button>}
      </div>
    </form>
  </article>;
}
